import { useContext } from 'react';

import { MessagingContext } from '@sorare/wallet-shared';
import type {
  PromptResetPassword,
  ResetPassword as ResetPasswordRequest,
} from '@sorare/wallet-shared';

import type { Data } from '#src/components/Form/index.tsx';
import Form from '#src/components/Form/index.tsx';
import useHasher from '#src/hooks/useHasher.ts';
import { validate, validatePasswordConfirmation } from '#src/lib/password.ts';

import type { Props } from './types.ts';

const fields = [
  [
    'newPassword',
    'password',
    {
      autoComplete: 'new-password',
    },
  ],
  ['confirmNewPassword', 'password'],
] as const;

type Field = (typeof fields)[number][0];

const ResetPassword = ({
  email,
  nickname,
  resetPasswordToken,
  onSuccess,
}: PromptResetPassword['request']['args'] & Props) => {
  const { sendRequest } = useContext(MessagingContext)!;
  const hasher = useHasher();

  const onSubmit = async ({ newPassword, confirmNewPassword }: Data<Field>) => {
    const differentPasswordsError = validatePasswordConfirmation(
      newPassword,
      confirmNewPassword
    );
    if (differentPasswordsError)
      return {
        newPassword: differentPasswordsError,
        confirmNewPassword: ' ',
      };

    const passwordError = await validate(email, nickname, newPassword);
    if (passwordError) {
      return { newPassword: passwordError };
    }

    const passwordHash = await hasher(newPassword);
    const { error } = await sendRequest<ResetPasswordRequest>('resetPassword', {
      passwordHash,
      resetPasswordToken,
    });

    if (error) {
      if (error.resetPasswordToken) {
        return {
          password: `reset_password_token ${error.resetPasswordToken}`,
        };
      }
      return { password: 'invalid token' };
    }
    return undefined;
  };

  return <Form inputs={fields} onSubmit={onSubmit} onSuccess={onSuccess} />;
};

export default ResetPassword;
